import {
  LegalClassification,
  TransactionStatus,
  CryptoTransfersTransactionStatus,
  InternalSavingsPlanTransactionState,
  StakingTransactionStatus,
  FiatTransactionStatus,
} from '@bts-web/data-layer/server';
import {
  getRoundedPrecision,
  intlAssetValueFormatting,
  intlFiatValueFormatting,
  MaximumFractionDigits,
  PrecisionFloat,
  timeScalarOrNull,
  type TimeScalar,
} from '@bts-web/utils-formatting';
import { TransactionDetailsFieldVariants } from '../TransactionDetailsField/TransactionDetailsField';
import { TransactionViewQueryData } from '../types';
import {
  getTransactionTranslationKeywordByType,
  TransactionDirection,
  transactionTypeByTypeName,
  TransactionTypes,
} from '../../transactions/types/transactions';
import { valueFromAssetToAsset } from '../../common/utils/amounts';
import { getAppConfig } from '@bts-web/core-app-config';
import { optionalTimeFields } from './detailFieldBuilders';
import { DetailEntity, GetTransactionDetailsFieldsParams } from './types';
import {
  assetAmountFormat,
  fiatAmountFormat,
  getTransactionTimeScalar,
  isBuyOrSellTransaction,
  isCorporateActionViewQueryNode,
  isCryptoActionViewQueryNode,
  isCryptoTransferTransaction,
  isInternalSavingsPlanItemType,
  isNUITransactionCategory,
  isFiatTransactionViewQueryNode,
  isStakingActionViewQueryNode,
  isStakingRewardActionViewQueryNode,
  isSwapViewQueryNode,
  isUnstakingActionViewQueryNode,
} from './transactionDetailsUtils';
import { WalletOwnerType } from '../../crypto-transfers/types';

const formatAssetPrice = (
  symbol: string,
  price: PrecisionFloat,
  locale: string,
  currency?: string,
) => {
  return `1 ${symbol} = ${intlAssetValueFormatting(getRoundedPrecision(price), {
    locale,
    currency,
    fractionDigits: price?.precision,
  })}`;
};

export const getTransactionDetailsFieldsData = ({
  translations,
  transaction,
  locale,
  currency,
  translationUtil,
  flags: {
    feature_showAssetClassification,
    feature_showTaxWitheldForCryptoTransactions,
  },
}: GetTransactionDetailsFieldsParams): DetailEntity[] => {
  const transactionType: TransactionTypes =
    transactionTypeByTypeName[
      transaction?.__typename as unknown as keyof typeof transactionTypeByTypeName
    ];

  const { isUAETenant } = getAppConfig();

  const transactionKey =
    getTransactionTranslationKeywordByType(transactionType);

  const transactionByTypeTranslation = translationUtil(transactionKey, {
    0: '',
    interpolation: {
      escapeValue: false,
    },
  })?.replace(/:/g, '');

  const translationByStatus: Record<
    | TransactionStatus
    | StakingTransactionStatus
    | CryptoTransfersTransactionStatus
    | InternalSavingsPlanTransactionState
    | FiatTransactionStatus,
    string
  > = {
    CREATED: translations.statusCreated,
    EXPIRED: translations.statusExpired,
    FAILED: translations.statusFailed,
    FINISHED: translations.statusFinished,
    IN_PROGRESS: translations.statusInProgress,
    CANCELED: translations.statusCancelled,
    PROCESSING: translations.statusProcessing,
    PENDING: translations.statusPending,
    ON_HOLD: translations.statusOnHold,
    REJECTED: translations.statusRejected,
    CONFIRMED: translations.statusConfirmed,
    SCHEDULED: translations.statusScheduled,
    SUCCESS: translations.statusFinished,
    NOT_CONFIRMED: translations.notConfirmed, // TODO update with right translations
    REFUNDED: translations.statusRefunded,
    // Fiat transaction statuses
    INITIATED: translations.statusCreated,
    APPROVED: translations.statusCompleted,
    CREDITED: translations.statusCompleted,
    CANCELLED: translations.statusCancelled,
    MANUAL_REVIEW: translations.statusPending,
  };

  const translationByLegalClassification: Record<LegalClassification, string> =
    {
      OTHER: translations.other,
      NONE: translations.none,
      ARTS: translations.arts,
      EMTS: translations.emts,
    };

  let transactionDetailsFields: Record<string, string | null> = {};

  let startedAt: TimeScalar | null = null;

  let transactionTime: TimeScalar | null = null;

  const isSwapTransaction = isSwapViewQueryNode(transaction);

  const isInternalSavingsPlanTransaction =
    isInternalSavingsPlanItemType(transaction);

  const isCryptoActionTransaction = isCryptoActionViewQueryNode(transaction);

  const isCorporateActionTransactionType =
    isCorporateActionViewQueryNode(transaction);

  if (transaction && !isInternalSavingsPlanTransaction) {
    transactionDetailsFields = {
      status: transaction?.status
        ? translationByStatus[
            transaction?.status as keyof typeof translationByStatus
          ]
        : null,
    };
  }

  if (isStakingRewardActionViewQueryNode(transaction)) {
    startedAt = getTransactionTimeScalar(transaction);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: transactionByTypeTranslation,
      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
      fiatAmount: fiatAmountFormat(transaction?.fiatAmount, locale, currency),
    };
  }

  if (
    isStakingActionViewQueryNode(transaction) ||
    isUnstakingActionViewQueryNode(transaction)
  ) {
    startedAt = getTransactionTimeScalar(transaction);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      to: `${transaction?.asset.name} ${translations.wallet}`,
      type: transactionByTypeTranslation,
      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
      fiatAmount: fiatAmountFormat(transaction?.fiatAmount, locale, currency),
    };
  }

  if (isInternalSavingsPlanTransaction) {
    startedAt = timeScalarOrNull(transaction?.createdAt);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      monthly: translations.monthly,
      type: transactionByTypeTranslation,
      state: transaction?.state ?? null,
      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
      currentValue:
        transaction?.asset?.symbol && transaction?.asset?.price
          ? formatAssetPrice(
              transaction.asset.symbol,
              transaction.asset.price,
              locale,
              currency,
            )
          : null,
      paymentMethod: transaction?.fiat?.name
        ? translationUtil('savings_plan_balance', {
            name: transaction.fiat.name,
          })
        : null,
    };
  }

  if (isSwapTransaction) {
    const { childrenTransactions } = transaction || {};

    const incomingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.INCOMING,
        )) ||
      null;

    const outgoingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.OUTGOING,
        )) ||
      null;

    const incomingAssetValue = valueFromAssetToAsset({
      amount: '1',
      fromPrice: outgoingTransaction?.assetPrice,
      toPrice: incomingTransaction?.assetPrice,
      customPrecision: MaximumFractionDigits.ASSET,
    });

    transactionTime = timeScalarOrNull(transaction?.time);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: translations.typeSwap,
      youPaid: `${intlAssetValueFormatting(
        Number(getRoundedPrecision(outgoingTransaction?.assetAmount)),
        {
          locale,
          fractionDigits: outgoingTransaction?.assetAmount?.precision ?? 8,
        },
      )} ${outgoingTransaction?.asset?.symbol}`,
      youReceive: `${intlAssetValueFormatting(
        Number(getRoundedPrecision(incomingTransaction?.assetAmount)),
        {
          locale,
          fractionDigits: incomingTransaction?.assetAmount?.precision ?? 8,
        },
      )} ${incomingTransaction?.asset?.symbol}`,
      price: `1 ${outgoingTransaction?.asset?.symbol} = ${intlAssetValueFormatting(
        Number(incomingAssetValue?.value),
        {
          locale,
          fractionDigits: incomingAssetValue?.precision,
        },
      )} ${incomingTransaction?.asset?.symbol}`,
      fee: fiatAmountFormat(transaction?.feesAfterCost, locale, currency),
      legalClassification:
        (incomingTransaction?.asset?.legalClassification &&
          translationByLegalClassification[
            incomingTransaction?.asset?.legalClassification
          ]) ||
        null,
      taxWithheld: fiatAmountFormat(transaction?.taxAmount, locale, currency),
      spread: fiatAmountFormat(transaction?.spread, locale, currency),
      //TODO: enable after we have BE support
      capitalGain: null,
      taxableAmount: null,
    };
  }

  if (isCryptoTransferTransaction(transaction)) {
    const isWithdrawalTransferType =
      transaction?.__typename === 'WithdrawalCryptoTransfersTransaction';

    const translationByOwnerType: Partial<Record<WalletOwnerType, string>> = {
      [WalletOwnerType.LegalEntity]: translations.ownerTypeLegalEntity,
      [WalletOwnerType.NaturalPerson]: translations.ownerTypeNaturalPerson,
    };

    const ownerType = transaction?.externalAddress?.ownerType;

    const isLegalEntity = ownerType === WalletOwnerType.LegalEntity;

    transactionTime = timeScalarOrNull(
      transaction?.status === 'FINISHED'
        ? transaction?.creditedAt
        : transaction?.createdAt,
    );

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: isWithdrawalTransferType
        ? translations.typeWithdraw
        : translations.typeDeposit,
      legalClassification:
        (transaction?.asset?.legalClassification &&
          translationByLegalClassification[
            transaction?.asset?.legalClassification
          ]) ||
        null,
      network: transaction?.network.name || null,
      to: `${transaction?.asset.name} ${translations.wallet}`,
      ...(ownerType
        ? {
            ownerType:
              translationByOwnerType[ownerType as WalletOwnerType] || null,
          }
        : {}),
      ...(isLegalEntity
        ? {
            registeredBusinessName:
              transaction?.externalAddress?.ownerLegalName || null,
            leiNumber: transaction?.externalAddress?.ownerIdentifier || null,
          }
        : {
            recipientName: transaction?.externalAddress?.ownerLegalName || null,
          }),
      walletAddress: transaction?.externalAddress?.address || null,
      hash: transaction?.hash || null,
      fee: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetFee,
        locale,
      ),
      total: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
    };
  }

  if (isBuyOrSellTransaction(transaction)) {
    transactionTime = timeScalarOrNull(transaction?.time);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: transactionByTypeTranslation,
      price:
        transaction?.asset?.symbol && transaction?.assetPrice
          ? formatAssetPrice(
              transaction?.asset?.symbol,
              transaction?.assetPrice,
              locale,
              currency,
            )
          : null,

      spread: fiatAmountFormat(transaction?.spread, locale, currency),

      legalClassification:
        (transaction?.asset?.legalClassification &&
          translationByLegalClassification[
            transaction?.asset?.legalClassification
          ]) ||
        null,

      ...(feature_showTaxWitheldForCryptoTransactions && transaction?.taxAmount
        ? {
            taxWithheld: intlFiatValueFormatting(
              getRoundedPrecision(transaction?.taxAmount),
              {
                locale,
                currency,
                fractionDigits: transaction?.taxAmount?.precision,
              },
            ),
          }
        : {}),

      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
    };
  }

  if (isNUITransactionCategory(transaction)) {
    transactionTime = timeScalarOrNull(transaction?.time);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: transactionByTypeTranslation,
      price:
        transaction?.asset?.symbol && transaction?.assetPrice
          ? formatAssetPrice(
              transaction?.asset?.symbol,
              transaction?.assetPrice,
              locale,
              currency,
            )
          : null,

      legalClassification:
        (transaction?.asset?.legalClassification &&
          translationByLegalClassification[
            transaction?.asset?.legalClassification
          ]) ||
        null,
      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
    };
  }

  if (isCryptoActionTransaction) {
    const { childrenTransactions } = transaction || {};

    const incomingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.INCOMING,
        )) ||
      null;

    const outgoingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.OUTGOING,
        )) ||
      null;

    transactionTime = timeScalarOrNull(transaction?.time);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: translations.typeCryptoAction,
      youPaid: `${intlAssetValueFormatting(
        Number(getRoundedPrecision(outgoingTransaction?.assetAmount)),
        {
          locale,
          fractionDigits: outgoingTransaction?.assetAmount?.precision ?? 8,
        },
      )} ${outgoingTransaction?.asset?.symbol}`,
      youReceive: `${intlAssetValueFormatting(
        Number(getRoundedPrecision(incomingTransaction?.assetAmount)),
        {
          locale,
          fractionDigits: incomingTransaction?.assetAmount?.precision ?? 8,
        },
      )} ${incomingTransaction?.asset?.symbol}`,
      legalClassification:
        (incomingTransaction?.asset?.legalClassification &&
          translationByLegalClassification[
            incomingTransaction?.asset?.legalClassification
          ]) ||
        null,
    };
  }

  if (isCorporateActionTransactionType) {
    const { childrenTransactions } = transaction || {};

    const incomingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.INCOMING,
        )) ||
      null;

    const outgoingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.OUTGOING,
        )) ||
      null;

    transactionTime = timeScalarOrNull(transaction?.time);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      type: transactionByTypeTranslation,
      youPaid: outgoingTransaction
        ? `${intlAssetValueFormatting(
            Number(getRoundedPrecision(outgoingTransaction?.assetAmount)),
            {
              locale,
              fractionDigits: outgoingTransaction?.assetAmount?.precision ?? 8,
            },
          )} ${outgoingTransaction?.asset?.symbol}`
        : null,
      youReceive: incomingTransaction
        ? `${intlAssetValueFormatting(
            Number(getRoundedPrecision(incomingTransaction?.assetAmount)),
            {
              locale,
              fractionDigits: incomingTransaction?.assetAmount?.precision ?? 8,
            },
          )} ${incomingTransaction?.asset?.symbol}`
        : null,
      assetAmount: assetAmountFormat(
        transaction?.asset?.symbol,
        transaction?.assetAmount,
        locale,
      ),
    };
  }

  if (isFiatTransactionViewQueryNode(transaction)) {
    transactionTime = timeScalarOrNull(
      transaction.finishedAt ?? transaction.createdAt,
    );

    const updatedBalanceValue =
      (transaction.amount?.value
        ? Number(getRoundedPrecision(transaction.amount))
        : 0) -
      (transaction.fee?.value
        ? Number(getRoundedPrecision(transaction.fee))
        : 0);

    transactionDetailsFields = {
      ...transactionDetailsFields,
      from: transaction.payoutAccount?.iban ?? null,
      to: transaction.fiat?.symbol
        ? `${transaction.fiat.symbol} ${translations.wallet}`
        : null,
      type:
        transaction.type === 'DEPOSIT'
          ? translations.typeDeposit
          : translations.typeWithdraw,
      fee: fiatAmountFormat(transaction.fee, locale, currency),
      updatedBalance: intlFiatValueFormatting(updatedBalanceValue, {
        locale,
        currency,
        fractionDigits: transaction.amount?.precision ?? 2,
      }),
    };
  }

  const assetAmountLabelMap: Record<string, string> = {
    StakeStakingTransaction: translations.staked,
    StakingRewardTransaction: translations.youReceived,
    UnstakeStakingTransaction: translations.unstaked,
    GiveawayTransaction: translations.youReceived,
  };

  const assetAmountLabel =
    assetAmountLabelMap[transaction?.__typename as string] ??
    translations.traded;

  const spreadLabel = isUAETenant
    ? translations.tradeFeeInclVAT
    : (transaction as TransactionViewQueryData)?.asset?.__typename ===
          'CryptoAsset' ||
        (transaction as TransactionViewQueryData)?.asset?.__typename ===
          'MetalAsset'
      ? translations.tradeFee
      : translations.spread;

  return [
    ...(transactionDetailsFields?.status
      ? [
          {
            kind: 'status' as const,
            label: translations.status,
            name: 'transaction-status',
            value: transactionDetailsFields.status,
            variant: isCryptoTransferTransaction(transaction)
              ? TransactionDetailsFieldVariants.CRYPTO_TRANSACTION_STATUS
              : TransactionDetailsFieldVariants.TRANSACTION_STATUS,
            transactionType,
            status: isInternalSavingsPlanItemType(transaction)
              ? transaction.state || null
              : transaction != null && 'status' in transaction
                ? transaction.status || null
                : null,
          } satisfies DetailEntity,
        ]
      : []),

    ...(transactionDetailsFields?.state
      ? [
          {
            kind: 'status' as const,
            label: translations.status,
            name: 'transaction-status',
            value: transactionDetailsFields.state,
            variant: TransactionDetailsFieldVariants.TRANSACTION_STATUS,
            transactionType,
            status: isInternalSavingsPlanItemType(transaction)
              ? transaction.state || null
              : null,
          } satisfies DetailEntity,
        ]
      : []),
    ...(transactionDetailsFields.type
      ? [
          {
            kind: 'text' as const,
            label: translations.type,
            name: 'transaction-type',
            value: transactionDetailsFields.type,
          },
        ]
      : []),
    ...(transactionDetailsFields?.network
      ? [
          {
            kind: 'text' as const,
            label: translations.network,
            name: 'transaction-network',
            value: transactionDetailsFields.network,
          },
        ]
      : []),
    ...(transactionDetailsFields?.from
      ? [
          {
            kind: 'text' as const,
            label: translations.from,
            name: 'transaction-from',
            value: transactionDetailsFields.from,
          },
        ]
      : []),
    ...(transactionDetailsFields?.to
      ? [
          {
            kind: 'text' as const,
            label: translations.to,
            name: 'transaction-to',
            value: transactionDetailsFields.to,
          },
        ]
      : []),
    ...(transactionDetailsFields?.ownerType
      ? [
          {
            kind: 'text' as const,
            label: translations.walletOwnerType,
            name: 'transaction-wallet-owner-type',
            value: transactionDetailsFields.ownerType,
          },
        ]
      : []),
    ...(transactionDetailsFields?.recipientName
      ? [
          {
            kind: 'text' as const,
            label: translations.recipientName,
            name: 'transaction-recipient-name',
            value: transactionDetailsFields.recipientName,
          },
        ]
      : []),
    ...(transactionDetailsFields?.registeredBusinessName
      ? [
          {
            kind: 'text' as const,
            label: translations.registeredBusinessName,
            name: 'transaction-registered-business-name',
            value: transactionDetailsFields.registeredBusinessName,
          },
        ]
      : []),
    ...(transactionDetailsFields?.leiNumber
      ? [
          {
            kind: 'text' as const,
            label: translations.leiNumber,
            name: 'transaction-lei-number',
            value: transactionDetailsFields.leiNumber,
          },
        ]
      : []),
    ...(transactionDetailsFields?.walletAddress
      ? [
          {
            kind: 'wallet-address' as const,
            label: translations.walletAddress,
            name: 'transaction-walletAddress',
            value: transactionDetailsFields.walletAddress,
          } satisfies DetailEntity,
        ]
      : []),
    ...(transactionDetailsFields?.hash
      ? [
          {
            kind: 'wallet-address' as const,
            label: translations.hash,
            name: 'transaction-hash',
            value: transactionDetailsFields.hash,
          } satisfies DetailEntity,
        ]
      : []),
    ...(transactionDetailsFields?.fee
      ? [
          {
            kind: 'text' as const,
            label: translations.fee,
            name: 'transaction-fee',
            value: transactionDetailsFields.fee,
          },
        ]
      : []),
    ...(transactionDetailsFields?.updatedBalance
      ? [
          {
            kind: 'text' as const,
            label: translationUtil('tx_details_updated_balance', {
              currency,
              interpolation: { escapeValue: false },
            }),
            name: 'transaction-updated-balance',
            value: transactionDetailsFields.updatedBalance,
          },
        ]
      : []),
    ...(feature_showAssetClassification &&
    transactionDetailsFields?.legalClassification
      ? [
          {
            kind: 'text' as const,
            label: translations.assetClassification,
            name: 'transaction-legal-classification',
            value: transactionDetailsFields.legalClassification,
          },
        ]
      : []),
    ...(transactionDetailsFields.youPaid
      ? [
          {
            kind: 'text' as const,
            label: translations.youPaid,
            name: 'transaction-you-paid',
            value: transactionDetailsFields.youPaid,
          },
        ]
      : []),
    ...(transactionDetailsFields.youReceive
      ? [
          {
            kind: 'text' as const,
            label: translations.youReceive,
            name: 'transaction-you-receive',
            value: transactionDetailsFields.youReceive,
          },
        ]
      : []),
    ...(transactionDetailsFields.currentValue
      ? [
          {
            kind: 'text' as const,
            label: translations.currentValue,
            name: 'asset-transaction-current-value',
            value: transactionDetailsFields.currentValue,
          },
        ]
      : []),
    ...optionalTimeFields(
      translations.startedAt,
      'transaction-start-date',
      startedAt,
      TransactionDetailsFieldVariants.TRANSACTION_DATE,
    ),
    ...optionalTimeFields(
      translations.date,
      'transaction-date-hour',
      transactionTime,
      TransactionDetailsFieldVariants.TRANSACTION_DATE_HOUR,
    ),
    ...(transactionDetailsFields.assetAmount
      ? [
          {
            kind: 'text' as const,
            label: assetAmountLabel,
            name: 'transaction-traded',
            value: transactionDetailsFields.assetAmount,
          },
        ]
      : []),
    ...(transactionDetailsFields.price
      ? [
          {
            kind: 'text' as const,
            label: translations.price,
            name: 'transaction-price',
            value: transactionDetailsFields.price,
          },
        ]
      : []),
    //TODO: enable after we have BE support
    // ...(transactionDetailsFields.capitalGain
    //   ? [
    //       {
    //         label: translations.capitalGain,
    //         name: 'transaction-capital-gain',
    //         value: transactionDetailsFields.capitalGain,
    //       },
    //     ]
    //   : []),
    ...(transactionDetailsFields.spread
      ? [
          {
            kind: 'text' as const,
            label: spreadLabel,
            name: 'transaction-spread',
            value: transactionDetailsFields.spread,
          },
        ]
      : []),
    //TODO: enable after we have BE support
    // ...(transactionDetailsFields.taxableAmount
    //   ? [
    //       {
    //         label: translations.taxableAmount,
    //         name: 'transaction-taxable-amount',
    //         value: transactionDetailsFields.taxableAmount,
    //       },
    //     ]
    //   : []),
    ...(transactionDetailsFields.taxWithheld
      ? [
          {
            kind: 'text' as const,
            label: translations.taxWithheld,
            name: 'tax-withheld',
            value: transactionDetailsFields.taxWithheld,
          },
        ]
      : []),
    ...(transactionDetailsFields.total
      ? [
          {
            kind: 'text' as const,
            label: translations.total,
            name: 'transaction-total',
            value: transactionDetailsFields.total,
          },
        ]
      : []),

    ...(transactionDetailsFields.monthly
      ? [
          {
            kind: 'text' as const,
            label: translations.frequency,
            name: 'savings-plan-frequency',
            value: translations.monthly,
          },
        ]
      : []),
    ...(transactionDetailsFields.paymentMethod
      ? [
          {
            kind: 'text' as const,
            label: translations.paymentMethod,
            name: 'savingsPlan-spread',
            value: transactionDetailsFields.paymentMethod,
          },
        ]
      : []),
  ] satisfies DetailEntity[];
};
